export const INK_COLOR_PRESETS = [
  { id: "purple", label: "Purple", value: "#9000ff" },
  { id: "red", label: "Red", value: "#c11b17" },
  { id: "teal", label: "Teal", value: "#00a896" },
  { id: "electric-blue", label: "Blue", value: "#0070ff" },
  { id: "black", label: "Black", value: "#000000" },
] as const;

export const DEFAULT_INK_COLOR = INK_COLOR_PRESETS[0].value;
const PREVIOUS_DEFAULT_INK_COLORS = new Set(["#653364", "#ad46ff"]);

const INK_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export function isInkColor(value: unknown): value is string {
  return typeof value === "string" && INK_COLOR_PATTERN.test(value);
}

/** Migrate session defaults only; saved custom document ink keeps its exact hue. */
export function normalizeStoredInkColor(value: unknown): string {
  if (!isInkColor(value)) return DEFAULT_INK_COLOR;
  const normalized = value.toLowerCase();
  return PREVIOUS_DEFAULT_INK_COLORS.has(normalized) ? DEFAULT_INK_COLOR : normalized;
}

function colorRgb(value: string): [number, number, number] {
  const color = isInkColor(value) ? value : DEFAULT_INK_COLOR;
  return [
    Number.parseInt(color.slice(1, 3), 16),
    Number.parseInt(color.slice(3, 5), 16),
    Number.parseInt(color.slice(5, 7), 16),
  ];
}

/** Recolors grayscale artwork while retaining its anti-aliased edges and shading. */
export function tintArtwork(image: HTMLImageElement, color: string, background: "white" | "transparent" = "white"): HTMLCanvasElement {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (!width || !height) throw new Error("Artwork has no drawable pixels.");

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Your browser could not prepare the image.");

  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height);
  const [red, green, blue] = colorRgb(color);

  for (let index = 0; index < pixels.data.length; index += 4) {
    const alpha = pixels.data[index + 3]! / 255;
    const luminance = (0.2126 * pixels.data[index]! + 0.7152 * pixels.data[index + 1]! + 0.0722 * pixels.data[index + 2]!) / 255;
    const coverage = (1 - luminance) * alpha;
    pixels.data[index] = background === "transparent" ? red : Math.round(red * coverage + 255 * (1 - coverage));
    pixels.data[index + 1] = background === "transparent" ? green : Math.round(green * coverage + 255 * (1 - coverage));
    pixels.data[index + 2] = background === "transparent" ? blue : Math.round(blue * coverage + 255 * (1 - coverage));
    pixels.data[index + 3] = background === "transparent" ? Math.round(coverage * 255) : 255;
  }

  context.putImageData(pixels, 0, 0);
  return canvas;
}
